import { faRectangleAd } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, Targeting } from '@feathr/blackbox';
import { Skeleton, Slider, Text } from '@feathr/components';
import { useOmniSliceTargeting } from '@feathr/extender/components/Targetings';
import type { ListResponse } from '@feathr/rachis';

import { formatReachEstimate } from './AdWizardAudienceStep.EstimatedReach.utils';

import * as styles from './AdWizardAudienceStep.css';

interface IDisplayReachEstimateProps {
  campaign: Campaign;
  displayReach: number | null;
  isLoading: boolean;
  targetings: ListResponse<Targeting>;
}

const SLIDER_DEFAULT_VALUE = 50;
const SLIDER_MAX_VALUE = 100;
const SLIDER_MIN_VALUE = 10;

function DisplayReachEstimate({
  campaign,
  displayReach,
  isLoading,
  targetings,
}: Readonly<IDisplayReachEstimateProps>): JSX.Element {
  const { t } = useTranslation();
  const { onChange, selectedPercentage } = useOmniSliceTargeting({
    campaign,
    targetings: targetings.models,
    defaultPercentage: SLIDER_DEFAULT_VALUE,
  });

  const reach = (displayReach ?? 0) * (selectedPercentage / 100);
  const formattedReach = `${formatReachEstimate(reach)} ${t('people')}`;

  const sliderMarks = useMemo(
    () => [
      { value: 10, label: t('Narrow') },
      { value: 20, label: <></> },
      { value: 30, label: <></> },
      { value: 40, label: <></> },
      { value: 50, label: <></> },
      { value: 60, label: <></> },
      { value: 70, label: <></> },
      { value: 80, label: <></> },
      { value: 90, label: <></> },
      { value: 100, label: t('Wide') },
    ],
    [t],
  );

  const currentMark = sliderMarks.find((mark) => mark.value === selectedPercentage);

  const label = (
    <div className={styles.channelReachRow}>
      <FontAwesomeIcon className={styles.displayAdIcon} icon={faRectangleAd} />
      <Text.DefaultMedium mantineComponent={'span'}>
        {t('Est. Display Ads Reach:')}
      </Text.DefaultMedium>
      {isLoading ? (
        <Skeleton width={100} />
      ) : (
        <Text.DefaultSemibold mantineComponent={'span'}>{formattedReach}</Text.DefaultSemibold>
      )}
    </div>
  );

  const helpText = <Text.SmallRegular>{t('Adjust the slider to refine')}</Text.SmallRegular>;

  return (
    <Slider
      dataName={'display-reach-slider'}
      disabled={isLoading}
      helpText={helpText}
      label={label}
      marks={sliderMarks}
      max={SLIDER_MAX_VALUE}
      min={SLIDER_MIN_VALUE}
      onChange={onChange}
      preventLabelOverflow={true}
      restrictToMarks={true}
      tickLabel={null}
      value={currentMark?.value}
    />
  );
}

export default observer(DisplayReachEstimate);
